// Where the "Add to Home Screen" help was opened from. `link` is the
// /?action=install address a studio prints as a QR code.
export type InstallSource = 'account_menu' | 'booking_receipt' | 'link';

// Chromium only, and not in lib.dom.
export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type HomeScreenAppState = {
  // The browser's own install dialog, held until the visitor asks for it.
  prompt: BeforeInstallPromptEvent | null;
  openFrom: InstallSource | null;
};

const SERVER_STATE: HomeScreenAppState = { prompt: null, openFrom: null };

let state = SERVER_STATE;
const listeners = new Set<() => void>();

const update = (next: Partial<HomeScreenAppState>) => {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
};

// At import, as early as the site's bundle runs: Chrome fires the event once,
// soon after load, and a listener added by a later effect can miss it. Held
// rather than left to Chrome's own banner, so the offer never pops up in the
// middle of a checkout and only ever comes from a button the visitor pressed.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    update({ prompt: event as BeforeInstallPromptEvent });
  });
}

export const openInstallHelp = (source: InstallSource) =>
  update({ openFrom: source });

export const closeInstallHelp = () => update({ openFrom: null });

// The dialog can be shown once; Chrome fires a new event if it may be offered
// again.
export const spendInstallPrompt = () => update({ prompt: null });

export const subscribeToHomeScreenApp = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getHomeScreenAppState = () => state;

export const getServerHomeScreenAppState = () => SERVER_STATE;
